/**
 * Utility functions for the VoiceAgent component
 */

import { ConnectionState } from 'livekit-client'

import { UI } from './constants'
import type { AgentState, Message, MessageRole, PopupPosition, VoiceConnectionState } from './types'

/**
 * Generate a unique ID for messages
 */
export function generateMessageId(): string {
  return `msg_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
}

/**
 * Format a timestamp for display
 */
export function formatTimestamp(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Convert LiveKit ConnectionState to our VoiceConnectionState
 */
export function mapConnectionState(state: ConnectionState): VoiceConnectionState {
  switch (state) {
    case ConnectionState.Connected:
      return 'connected' as VoiceConnectionState
    case ConnectionState.Connecting:
      return 'connecting' as VoiceConnectionState
    case ConnectionState.Reconnecting:
      return 'reconnecting' as VoiceConnectionState
    case ConnectionState.Disconnected:
      return 'disconnected' as VoiceConnectionState
    default:
      return 'disconnected' as VoiceConnectionState
  }
}

/**
 * Get human-readable connection state message
 */
export function getConnectionStateMessage(state: VoiceConnectionState): string {
  switch (state) {
    case 'initializing':
      return 'Initializing...'
    case 'connecting':
      return 'Connecting...'
    case 'connected':
      return 'Connected'
    case 'disconnected':
      return 'Disconnected'
    case 'error':
      return 'Connection error'
    default:
      return 'Unknown'
  }
}

/**
 * Get agent state icon or emoji
 */
export function getAgentStateIcon(state: AgentState): string {
  switch (state) {
    case 'disconnected':
      return '○'
    case 'connecting':
      return '◐'
    case 'initializing':
      return '◑'
    case 'listening':
      return '●'
    case 'thinking':
      return '◈'
    case 'speaking':
      return '♫'
    default:
      return '○'
  }
}

/**
 * Build API endpoint with parameters
 */
export function buildEndpoint(template: string, params: Record<string, string>): string {
  let endpoint = template
  Object.entries(params).forEach(([key, value]) => {
    endpoint = endpoint.replace(`:${key}`, value)
  })
  return endpoint
}

/**
 * Check if we should auto-scroll transcript
 */
export function shouldAutoScroll(element: HTMLElement): boolean {
  const { scrollTop, scrollHeight, clientHeight } = element
  const distanceFromBottom = scrollHeight - scrollTop - clientHeight
  return distanceFromBottom <= UI.TRANSCRIPT_AUTO_SCROLL_THRESHOLD
}

/**
 * Scroll element to bottom smoothly
 */
export function scrollToBottom(element: HTMLElement, smooth = true): void {
  element.scrollTo({
    top: element.scrollHeight,
    behavior: smooth ? 'smooth' : 'auto',
  })
}

/**
 * Group consecutive messages by role
 */
export function groupMessagesByRole(messages: Message[]): Message[][] {
  const grouped: Message[][] = []
  let currentGroup: Message[] = []

  messages.forEach((message) => {
    if (currentGroup.length === 0 || currentGroup[0].role === message.role) {
      currentGroup.push(message)
    } else {
      grouped.push(currentGroup)
      currentGroup = [message]
    }
  })

  if (currentGroup.length > 0) {
    grouped.push(currentGroup)
  }

  return grouped
}

/**
 * Calculate audio level from audio data
 */
export function calculateAudioLevel(data: Float32Array): number {
  let sum = 0
  for (let i = 0; i < data.length; i++) {
    sum += data[i] * data[i]
  }
  const rms = Math.sqrt(sum / data.length)
  const level = Math.min(1, rms * 10)
  return level
}

/**
 * Get microphone audio constraints
 */
export function getAudioConstraints(): MediaStreamConstraints {
  return {
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      sampleRate: 48000,
    },
    video: false,
  }
}

/**
 * Parse JWT token to get expiry
 */
export function parseJwtExpiry(token: string): Date | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null

    // Use Buffer or TextDecoder for base64 decoding
    const base64 = parts[1]
    const base64Decoded = typeof Buffer !== 'undefined' ? Buffer.from(base64, 'base64').toString('utf-8') : atob(base64)

    const payload = JSON.parse(base64Decoded)
    if (!payload.exp) return null

    return new Date(payload.exp * 1000)
  } catch (error) {
    console.error('Failed to parse JWT:', error)
    return null
  }
}

/**
 * Check if token is expired or about to expire
 */
export function isTokenExpired(token: string, bufferMs = 60000): boolean {
  const expiry = parseJwtExpiry(token)
  if (!expiry) return true

  const now = new Date()
  const expiryWithBuffer = new Date(expiry.getTime() - bufferMs)
  return now >= expiryWithBuffer
}

/**
 * Get position styles for popup mode
 */
export function getPopupPositionStyles(position: PopupPosition): React.CSSProperties {
  const base: React.CSSProperties = {
    position: 'fixed',
    zIndex: 9999,
  }

  switch (position) {
    case 'bottom-right':
      return { ...base, bottom: 20, right: 20 }
    case 'bottom-left':
      return { ...base, bottom: 20, left: 20 }
    case 'top-right':
      return { ...base, top: 20, right: 20 }
    case 'top-left':
      return { ...base, top: 20, left: 20 }
    default:
      return { ...base, bottom: 20, right: 20 }
  }
}

// Re-export from shared utils
export { loadPreference, savePreference } from '@/src/utils/StorageUtils'

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

/**
 * Throttle function
 */
export function throttle<T extends (...args: any[]) => any>(func: T, limit: number): (...args: Parameters<T>) => void {
  let inThrottle = false

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => {
        inThrottle = false
      }, limit)
    }
  }
}

/**
 * Format role for display
 */
export function formatRole(role: MessageRole): string {
  switch (role) {
    case 'user':
      return 'You'
    case 'agent':
      return 'Agent'
    case 'system':
      return 'System'
    default:
      return role
  }
}

/**
 * Check if browser supports required features
 */
export function checkBrowserSupport(): { supported: boolean; missing: string[] } {
  const missing: string[] = []

  if (!navigator.mediaDevices?.getUserMedia) {
    missing.push('getUserMedia')
  }

  if (!window.RTCPeerConnection) {
    missing.push('WebRTC')
  }

  if (!window.WebSocket) {
    missing.push('WebSocket')
  }

  if (!window.AudioContext && !(window as any).webkitAudioContext) {
    missing.push('Web Audio API')
  }

  return {
    supported: missing.length === 0,
    missing,
  }
}

/**
 * Request microphone permission
 */
export async function requestMicrophonePermission(): Promise<boolean> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    // Stop the stream immediately after getting permission
    stream.getTracks().forEach((track) => track.stop())
    return true
  } catch (error) {
    console.error('Microphone permission denied:', error)
    return false
  }
}

/**
 * Get available audio input devices
 */
export async function getAudioInputDevices(): Promise<MediaDeviceInfo[]> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter((device) => device.kind === 'audioinput')
  } catch (error) {
    console.error('Failed to enumerate devices:', error)
    return []
  }
}

/**
 * Format error message for display
 */
export function formatErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  return 'An unexpected error occurred'
}

/**
 * Create a rate limiter
 */
export function createRateLimiter(maxCalls: number, windowMs: number) {
  const calls: number[] = []

  return function isAllowed(): boolean {
    const now = Date.now()
    const windowStart = now - windowMs

    // Remove old calls outside the window
    while (calls.length > 0 && calls[0] < windowStart) {
      calls.shift()
    }

    // Check if we can make a new call
    if (calls.length < maxCalls) {
      calls.push(now)
      return true
    }

    return false
  }
}

/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * Linear interpolation
 */
export function lerp(start: number, end: number, factor: number): number {
  return start + (end - start) * factor
}

/**
 * Check if running in embedded mode (iframe or shadow DOM)
 */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top
  } catch {
    // Cross-origin iframe
    return true
  }
}

/**
 * Adjusts color brightness - makes it darker or lighter
 * @param hex - Hex color string (with or without #)
 * @param percent - Positive to lighten, negative to darken
 */
export function adjustColorBrightness(hex: string, percent: number): string {
  // Remove # if present
  hex = hex.replace(/^#/, '')

  // Parse r, g, b values
  let r = parseInt(hex.substring(0, 2), 16)
  let g = parseInt(hex.substring(2, 4), 16)
  let b = parseInt(hex.substring(4, 6), 16)

  // Adjust brightness
  r = Math.max(0, Math.min(255, Math.floor(r * (1 + percent / 100))))
  g = Math.max(0, Math.min(255, Math.floor(g * (1 + percent / 100))))
  b = Math.max(0, Math.min(255, Math.floor(b * (1 + percent / 100))))

  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}
